import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Building, MapPin, Phone, Mail, Home, AlertCircle, Trash2, Navigation } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { TradesmanPortfolioUpload } from "./TradesmanPortfolioUpload";
import { TradesmanLogoUpload } from "./TradesmanLogoUpload";
import { GooglePlacesAddress } from "./GooglePlacesAddress";
import { GooglePlacesLocation } from "./GooglePlacesLocation";

interface TradesmanProfileEditProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProfileUpdated: () => void;
  onSignOut?: () => void;
}

const availableJobCategories = [
  "Plumbing", "Electrical", "Carpentry", "HVAC", "Painting", "Roofing", 
  "Flooring", "Landscaping", "Cleaning", "General Maintenance", 
  "Masonry", "Locksmith", "Appliance Repair", "Pest Control",
  "Plastering", "Dry-lining", "Wallpapering", "Welding", "Scaffolding"
];

export const TradesmanProfileEdit = ({ open, onOpenChange, onProfileUpdated, onSignOut }: TradesmanProfileEditProps) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [location, setLocation] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [serviceRadius, setServiceRadius] = useState(25);
  const [tradesmanId, setTradesmanId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchProfile();
    }
  }, [open]);

  const fetchProfile = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.user.id)
        .single();

      if (profile) {
        setTradesmanId(profile.id);
        setSelectedCategories(profile.job_categories || []);
        setCompanyName(profile.company_name || "");
        setPhone(profile.phone || "");
        setEmail(profile.email || "");
        setAddress(profile.address || "");
        setLocation(profile.location || "");
        setServiceRadius(profile.service_radius || 25);
        setLogoUrl(profile.logo_url || null);
      }
    } catch (error: any) {
      toast({
        title: "Error loading profile",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else if (prev.length < 5) {
        return [...prev, category];
      } else {
        toast({
          title: "Maximum categories reached",
          description: "You can select up to 5 categories. Remove one to add another.",
          variant: "destructive",
        });
        return prev;
      }
    });
  };

  const validateUKPhone = (phone: string) => {
    // UK phone number regex - accepts landline and mobile formats
    const ukPhoneRegex = /^(?:(?:\+44)|(?:0))(?:(?:7[0-9]{9})|(?:1[0-9]{9})|(?:2[0-9]{9})|(?:3[0-9]{9})|(?:8[0-8][0-9]{8})|(?:9[0-9]{9}))$/;
    return ukPhoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (selectedCategories.length === 0) {
      toast({
        title: "Please select at least one category",
        description: "You must select at least one job category.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    if (!validateUKPhone(phone)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid UK phone number (e.g., 07123456789 or 01234567890).",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("profiles")
        .update({
          company_name: companyName,
          phone,
          email,
          address,
          location,
          service_radius: serviceRadius,
          job_categories: selectedCategories,
        })
        .eq("user_id", user.user.id);

      if (error) throw error;

      toast({
        title: "Profile updated successfully!",
        description: "Your changes have been saved.",
      });

      onProfileUpdated();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase.functions.invoke('delete-user-account');
      
      if (error) throw error;
      
      toast({
        title: "Account deleted successfully",
        description: "Your account and all associated data have been permanently deleted.",
      });
      
      // Sign out the user
      await supabase.auth.signOut();
      if (onSignOut) {
        onSignOut();
      }
      
      onOpenChange(false);
    } catch (error: any) {
      console.error('Delete account error:', error);
      toast({
        title: "Error deleting account",
        description: error.message || "Failed to delete account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="left-0 top-0 translate-x-0 translate-y-0 w-screen h-[100svh] max-w-none max-h-none m-0 p-4 rounded-none overflow-y-auto overflow-x-hidden sm:left-1/2 sm:top-1/2 sm:translate-x-[-50%] sm:translate-y-[-50%] sm:w-[95vw] sm:h-[90vh] sm:max-w-4xl sm:max-h-[90vh] sm:rounded-lg sm:m-auto"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        onInteractOutside={(e) => {
          const target = (e.target as HTMLElement) || (e as any).detail?.originalEvent?.target;
          if (target instanceof HTMLElement && (target.closest('.pac-container') || target.classList.contains('pac-container'))) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          const target = (e.target as HTMLElement) || (e as any).detail?.originalEvent?.target;
          if (target instanceof HTMLElement && (target.closest('.pac-container') || target.classList.contains('pac-container'))) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Your Profile</DialogTitle>
          <DialogDescription>
            Update your trade specialties, business information, and portfolio
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-1 gap-1 h-auto mb-4 p-1">
            <TabsTrigger value="profile" className="text-sm py-2.5 px-3 w-full">Profile & Specialties</TabsTrigger>
            <TabsTrigger value="portfolio" className="text-sm py-2.5 px-3 w-full">Portfolio & Insurance</TabsTrigger>
            <TabsTrigger value="account" className="text-sm py-2.5 px-3 w-full">Account Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4 mt-0">

            <form id="tradesman-profile-form" onSubmit={handleSubmit} className="space-y-4 pb-32 sm:pb-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g., Smith Plumbing Services"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    placeholder="e.g., john@smithplumbing.co.uk"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    type="tel"
                    placeholder="e.g., 07123456789 or 01234567890"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

                <div className="space-y-2">
                <Label htmlFor="address">Business Address</Label>
                <div className="relative">
                  <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 z-10" />
                  <GooglePlacesAddress
                    id="address"
                    value={address}
                    onChange={setAddress}
                    placeholder="e.g., 123 High Street, London, SW1A 1AA"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

                <div className="space-y-2">
                <Label htmlFor="location">Service Area</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 z-10" />
                  <GooglePlacesLocation
                    id="location"
                    value={location}
                    onChange={setLocation}
                    placeholder="e.g., London, Manchester, Birmingham"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="serviceRadius">Service Radius: {serviceRadius} miles</Label>
                <div className="relative">
                  <Navigation className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 z-10" />
                  <div className="pl-10 pr-4 py-3 border border-input bg-background rounded-md">
                    <Slider
                      id="serviceRadius"
                      min={5}
                      max={50}
                      step={5}
                      value={[serviceRadius]}
                      onValueChange={(value) => setServiceRadius(value[0])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>5 miles</span>
                      <span>50 miles</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  This sets how far you're willing to travel for jobs. You'll only see jobs within this radius.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Job Categories ({selectedCategories.length}/5)
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    Choose up to 5
                  </span>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {availableJobCategories.map((category) => {
                    const isSelected = selectedCategories.includes(category);
                    const isDisabled = !isSelected && selectedCategories.length >= 5;
                    
                    return (
                      <Button
                        key={category}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        disabled={isDisabled}
                        onClick={() => handleCategoryToggle(category)}
                        className={`h-auto p-2 text-left justify-start transition-all ${
                          isSelected ? "bg-primary text-primary-foreground" : ""
                        } ${isDisabled ? "opacity-50" : ""}`}
                      >
                        <div className="flex items-center gap-1.5 w-full">
                          {isSelected && <CheckCircle className="h-3 w-3" />}
                          <span className="text-xs font-medium leading-tight">
                            {category}
                          </span>
                        </div>
                      </Button>
                    );
                  })}
                </div>

                {selectedCategories.length > 0 && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-medium text-foreground mb-2">Selected Categories:</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedCategories.map((category) => (
                        <Badge key={category} variant="secondary">
                          {category}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </form>

            <div className="mt-6 pt-4 border-t bg-background">
              <div className="flex flex-col gap-2">
                <Button
                  type="submit"
                  form="tradesman-profile-form"
                  disabled={isLoading || selectedCategories.length === 0}
                  variant="hero"
                  className="w-full"
                >
                  {isLoading ? "Updating..." : "Save Changes"}
                </Button>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full">
                  Cancel
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="portfolio" className="space-y-4 mt-0">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1 text-sm">Important: Required Information</h4>
                  <div className="text-xs text-blue-800 space-y-1">
                    <p><strong>Portfolio Photos:</strong> You can upload photos with or without descriptions.</p>
                    <p><strong>Insurance Certificate:</strong> You must provide the expiry date before uploading.</p>
                    <p>Both items require admin approval.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <TradesmanLogoUpload 
              currentLogoUrl={logoUrl}
              onLogoUpdate={(url) => setLogoUrl(url)}
            />
            
            {tradesmanId && (
              <TradesmanPortfolioUpload
                tradesmanId={tradesmanId}
                onUploadComplete={onProfileUpdated}
                onValidationChange={() => {}}
              />
            )}
          </TabsContent>

          <TabsContent value="account" className="space-y-4 mt-0">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-foreground mb-2">Danger Zone</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  These actions are permanent and cannot be undone.
                </p>
              </div>
              
              <div className="border border-destructive/20 rounded-lg p-3 sm:p-4 bg-destructive/5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-destructive mb-2">Delete Account</h4>
                    <p className="text-xs text-muted-foreground mb-4 sm:mb-0">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                     <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                       <AlertDialogTrigger asChild>
                         <Button 
                           variant="destructive" 
                           size="sm"
                           disabled={isDeleting}
                           className="w-full sm:w-auto"
                         >
                           <Trash2 className="h-4 w-4 mr-2" />
                           Delete Account
                         </Button>
                       </AlertDialogTrigger>
                       <AlertDialogContent>
                         <AlertDialogHeader>
                           <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                           <AlertDialogDescription className="space-y-2">
                             <p>This action cannot be undone. This will permanently delete your account and remove all your data from our servers, including:</p>
                             <ul className="list-disc list-inside space-y-1 text-sm">
                               <li>Your profile and personal information</li>
                               <li>All job responses and messages</li>
                               <li>Portfolio photos and insurance certificates</li>
                               <li>All ratings and reviews</li>
                               <li>Job history and payment records</li>
                             </ul>
                             <p className="font-semibold text-destructive mt-3">
                               Are you sure you want to permanently delete your tradesman account?
                             </p>
                           </AlertDialogDescription>
                         </AlertDialogHeader>
                         <AlertDialogFooter>
                           <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                           <AlertDialogAction
                             onClick={handleDeleteAccount}
                             disabled={isDeleting}
                             className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                           >
                             {isDeleting ? "Deleting..." : "Yes, delete my account"}
                           </AlertDialogAction>
                         </AlertDialogFooter>
                       </AlertDialogContent>
                     </AlertDialog>
                   </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};